import { Anchor, Autocomplete, P } from '@dnb/eufemia';
import { AutocompleteTypeEvent } from '@dnb/eufemia/components/autocomplete';
import { DrawerListDataArrayObjectStrict } from '@dnb/eufemia/fragments/drawer-list';
import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { backendClient } from '@/api/backendClient';
import { SearchResult } from '@/openapi/schema';
import { isEmptyString } from '@/utils/textUtils';

const typeToBadgeText = {
  API: 'API',
  EVENT: 'Event',
  APP: 'App',
  TEAM: 'Team',
  CLIENT: 'Client',
  SCOPE: 'Scope',
};

const typeToBadgeColor = {
  API: 'bg-summer-green',
  EVENT: 'bg-sky-blue',
  APP: 'bg-blue-500',
  TEAM: 'bg-sea-green',
  CLIENT: 'bg-purple-700',
  SCOPE: 'bg-indigo-800',
};

function TypeBadge({ type }: { type: SearchResult['type'] }) {
  const badgeText = typeToBadgeText[type];
  const badgeColor = typeToBadgeColor[type];

  return (
    <P className={`text-white text-center ${badgeColor} w-14 py-1 rounded-lg`} size="x-small" weight="medium">
      {badgeText}
    </P>
  );
}

const onTypeHandler = ({
  value,
  showIndicator,
  hideIndicator,
  updateData,
  debounce,
  showNoOptionsItem,
}: AutocompleteTypeEvent) => {
  showIndicator();
  debounce(
    async ({ value }) => {
      const results = await getResults(value);
      const newData = results.map(
        (result): DrawerListDataArrayObjectStrict => ({
          selectedKey: result.path,
          content: getContent(result),
          suffix_value: <TypeBadge type={result.type} />,
        }),
      );
      if (newData.length === 0) {
        showNoOptionsItem();
      } else {
        updateData(newData);
      }
      hideIndicator();
    },
    {
      value,
    },
    300,
  );
};

async function getResults(query: string): Promise<SearchResult[]> {
  if (isEmptyString(query)) return [];
  const { data } = await backendClient.GET('/search/fuzzy', { params: { query: { query } } });
  return data?.results ?? [];
}

function getContent(result: SearchResult) {
  const name = result.name ?? `Unnamed ${typeToBadgeText[result.type]}`;
  const anchor = (
    <Anchor element={Link} to={result.path}>
      {name}
    </Anchor>
  );
  return isEmptyString(result.description) ? [anchor] : [anchor, result.description];
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(undefined);

  const shortcut = useMemo(() => {
    return navigator.userAgent.includes('Mac') ? '⌘K' : 'Ctrl+K';
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Autocomplete
      disable_filter
      input_ref={inputRef}
      mode="async"
      no_options="No results found."
      on_select={({ value }) => {
        if (value) navigate(value);
      }}
      on_type={onTypeHandler}
      placeholder={
        <div className="flex justify-between">
          <span className="text-emerald-green">Search ... </span>
          <span>({shortcut})</span>
        </div>
      }
      prevent_selection
      size="medium"
      stretch
    />
  );
}
